import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useEquipmentStore } from "../../store/store";
import { usePermissions } from "../../hooks/usePermission";
import { ASSETS_EQUIPMENT } from "../../constants/modules";
import images from "../../assets/images";
import { DotLoader } from "../../components/loader/Loader";

interface EquipmentTypeCard {
  id: string;
  name: string;
  description: string;
  icon: string;
  count?: number;
}

const EquipmentTypesLanding: React.FC = () => {
  const navigate = useNavigate();
  const { equipmentData, allAssetsEquipment, loading } = useEquipmentStore();
  const { canRead } = usePermissions();
  const [equipmentTypes, setEquipmentTypes] = useState<EquipmentTypeCard[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await allAssetsEquipment();
      } catch (error) {
        console.error("Error fetching equipment data:", error);
      }
    };

    fetchData();
  }, [allAssetsEquipment]);

  useEffect(() => {
    if (equipmentData && equipmentData.length > 0) {
      const typeGroups = equipmentData.reduce(
        (acc: Record<string, number>, equipment: any) => {
          const typeName = equipment.equipmentName || "Other";
          acc[typeName] = (acc[typeName] || 0) + 1;
          return acc;
        },
        {},
      );
      const types: EquipmentTypeCard[] = Object.entries(typeGroups).map(
        ([name, count]) => ({
          id: name.toLowerCase().replace(/\s+/g, "-"),
          name,
          description: `Manage ${name.toLowerCase()} inventory`,
          icon: getEquipmentIcon(name),
          count: count as number,
        }),
      );
      setEquipmentTypes(types);
    } else {
    }
  }, [equipmentData]);

  const getEquipmentIcon = (equipmentName: string): string => {
    const name = equipmentName.toLowerCase();

    if (name.includes("fire extinguisher") || name.includes("extinguisher")) {
      return images.fireExtinguisher;
    } else if (name.includes("fire door") || name.includes("door")) {
      return images.fireShutter;
    } else if (name.includes("smoke detector") || name.includes("detector")) {
      return images.apDetectionIcon;
    } else if (name.includes("sprinkler")) {
      return images.sprinklerSystem;
    } else if (name.includes("alarm")) {
      return images.apFireAlarm;
    } else if (name.includes("hose") || name.includes("hydrant")) {
      return images.reeksHydrants;
    } else if (name.includes("pump")) {
      return images.firefighting;
    } else if (name.includes("panel") || name.includes("control")) {
      return images.apDetectionIcon;
    } else if (name.includes("suppression")) {
      return images.gasSuppression;
    } else if (name.includes("smoke control")) {
      return images.smokeControl;
    } else if (name.includes("emergency")) {
      return images.emergencyLight;
    }

    return images.assetsIcon;
  };

  const handleEquipmentTypeClick = (equipmentType: EquipmentTypeCard) => {
    try {
      navigate(`/safety-equipment/${equipmentType.id}`, {
        state: {
          equipmentTypeName: equipmentType.name,
          equipmentTypeId: equipmentType.id,
        },
      });
    } catch (error) {
      console.error("Navigation error:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <DotLoader />
      </div>
    );
  }

  if (!canRead(ASSETS_EQUIPMENT)) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500 text-lg">
          You don't have permission to view equipment data.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full py-4 px-2">
      <div className="w-full mt-20 mb-12">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Fire Safety Equipment Management
            </h2>
            <p className="text-gray-600">
              Select an equipment type to view and manage inventory
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() =>
                navigate("/safety-equipment/fire-safety-mis-checklist")
              }
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              Fire Safety MIS Checklist
            </button>

            <button
              onClick={() => navigate("/safety-equipment/kpi-checklist")}
              className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-semibold hover:from-green-700 hover:to-green-800 transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                />
              </svg>
              Fire Safety KPI Checklist
            </button>

            <button
              onClick={() =>
                navigate("/safety-equipment/fire-safety-scorecard")
              }
              className="px-6 py-3 bg-gradient-to-r from-yellow-600 to-yellow-700 text-white rounded-lg font-semibold hover:from-yellow-700 hover:to-yellow-800 transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
              title="View and manage Fire Safety scorecards"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                />
              </svg>
              Fire Safety Scorecard
            </button>

            <button
              onClick={() =>
                navigate("/safety-equipment/electrical-safety-scorecard")
              }
              className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg font-semibold hover:from-red-700 hover:to-red-800 transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
              title="View and manage Electrical Safety Scorecards"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              Electrical Safety Scorecard
            </button>

            <button
              onClick={() =>
                navigate("/safety-equipment/split-ac-checklist")
              }
              className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-lg font-semibold hover:from-indigo-700 hover:to-indigo-800 transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
              title="View and manage Split AC Checklist"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
                />
              </svg>
              Split AC Checklist
            </button>
          </div>
        </div>
      </div>

      <div className="w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {equipmentTypes.map((equipmentType) => (
            <div
              key={equipmentType.id}
              onClick={() => handleEquipmentTypeClick(equipmentType)}
              className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-105 border border-gray-200 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <img
                      src={equipmentType.icon}
                      alt={equipmentType.name}
                      className="w-8 h-8 object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = images.assetsIcon;
                      }}
                    />
                  </div>
                  {/* {equipmentType.count && (
                  <span className="bg-blue-600 text-white text-sm font-medium px-3 py-1 rounded-full">
                    {equipmentType.count} items
                  </span>
                )} */}
                </div>

                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  {equipmentType.name}
                </h3>

                <p className="text-gray-600 text-sm leading-relaxed">
                  {equipmentType.description}
                </p>

                <div className="mt-4 flex items-center text-[#00A8A5] font-medium">
                  <span>Manage Equipment</span>
                  <svg
                    className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {equipmentTypes.length === 0 && (
        <div className="text-center py-12">
          <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <img
              src={images.assetsIcon}
              alt="No equipment"
              className="w-12 h-12 opacity-50"
            />
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            No Equipment Types Found
          </h3>
          <p className="text-gray-500">
            No equipment data is available. Please add equipment inventory
            first.
          </p>
        </div>
      )}

      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => navigate("/safety-equipment/checklist-management")}
          className="w-16 h-16 bg-purple-600 hover:bg-purple-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center"
          title="Manage Inspection Checklists"
        >
          <svg
            className="w-8 h-8"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
        </button>
      </div>

      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <svg
              className="w-6 h-6 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-blue-800 mb-1">
              Web Inspection Process
            </h3>
            <p className="text-sm text-blue-700 mb-2">
              For web-based inspections, click on any equipment type above, then
              use the "Inspect Asset" button next to each asset. The system will
              automatically load the appropriate checklist based on the asset
              type (CO2, Chemical, etc.).
            </p>
            <div className="text-xs text-blue-600">
              <span className="font-medium">Mobile users:</span> Use the QR
              scanner button for field inspections
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquipmentTypesLanding;
